import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { AlertCircle, CheckCircle, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateForDisplay } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANCY_SCOPED_DOCUMENT_TYPES = new Set([
  'DepositProtection',
  'PrescribedInfo',
  'TenancyAgreement',
  'GuarantorAgreement',
  'InventoryCheckIn',
  'InventoryCheckOut',
  'Section8Notice',
  'Section13Notice',
  'HowToRent',
  'RRAInformationSheet',
  'DispatchReceipt',
  'RentSchedule',
  'HoldingDepositAgreement',
  'ArrearsLetter',
  'RightToRentDoc',
  'RightToRentID',
  'EmploymentReference',
  'PreviousLandlordReference',
  'TenantInsurance',
]);

const isObjectIdLike = (value) => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

// Document types organized by category
const DOCUMENT_TYPES = {
  'Compliance': [
    { value: 'EPC', label: 'EPC (Energy Performance)', expiryMonths: 120 },
    { value: 'GasSafety', label: 'Gas Safety Certificate', expiryMonths: 12 },
    { value: 'EICR', label: 'Electrical Installation', expiryMonths: 60 },
    { value: 'RRAInformationSheet', label: 'RRA Information Sheet (Renters’ Rights Act)' },
    { value: 'ICORegistration', label: 'ICO Registration' },
    { value: 'DepositProtection', label: 'Deposit Protection Scheme' },
    { value: 'PrescribedInfo', label: 'Prescribed Information' },
    { value: 'LegionellaAssessment', label: 'Legionella Assessment', expiryMonths: 12 },
    { value: 'HMOLicence', label: 'HMO Licence', expiryMonths: 12 },
    { value: 'FireRiskAssessment', label: 'Fire Risk Assessment', expiryMonths: 12 },
    { value: 'PATTesting', label: 'PAT Testing', expiryMonths: 12 },
  ],
  'Tenancy': [
    { value: 'TenancyAgreement', label: 'Tenancy Agreement' },
    { value: 'GuarantorAgreement', label: 'Guarantor Agreement' },
    { value: 'InventoryCheckIn', label: 'Inventory Check-in' },
    { value: 'InventoryCheckOut', label: 'Inventory Check-out' },
    { value: 'Section8Notice', label: 'Section 8 Notice' },
    { value: 'Section13Notice', label: 'Section 13 Notice' },
    { value: 'HowToRent', label: 'How To Rent Guide' },
    { value: 'DispatchReceipt', label: 'Document Delivery Record (PDF)' },
  ],
  'Financial': [
    { value: 'RentSchedule', label: 'Rent Schedule' },
    { value: 'HoldingDepositAgreement', label: 'Holding Deposit Agreement' },
    { value: 'ArrearsLetter', label: 'Arrears Letter' },
  ],
  'Tenant Provided': [
    { value: 'RightToRentDoc', label: 'Right to Rent Document' },
    { value: 'RightToRentID', label: 'Right to Rent ID (RTR)' },
    { value: 'EmploymentReference', label: 'Employment Reference' },
    { value: 'PreviousLandlordReference', label: 'Previous Landlord Reference' },
    { value: 'TenantInsurance', label: 'Tenant Insurance' },
  ],
  'Other': [
    { value: 'Other', label: 'Other Document' },
  ],
};

// Helper to find document config and check if it has expiry
const getDocumentConfig = (docType) => {
  for (const category of Object.values(DOCUMENT_TYPES)) {
    const doc = category.find(d => d.value === docType);
    if (doc) return doc;
  }
  return null;
};

const shouldShowByRule = (docType, property) => {
  if (!property) return true;

  if (docType === 'GasSafety') {
    return Boolean(property.has_gas_supply);
  }
  if (docType === 'HMOLicence') {
    return property.property_type === 'hmo';
  }
  if (docType === 'FireRiskAssessment') {
    return property.property_type === 'hmo' || Number(property.floors || 0) >= 7;
  }
  if (docType === 'PATTesting') {
    return Boolean(property.furnished_appliances);
  }
  if (docType === 'LegionellaAssessment') {
    return ['stored_water', 'complex_system'].includes(property.water_system_type);
  }
  return true;
};

const getDynamicDocumentTypes = (property, compliance) => {
  const allDocs = Object.entries(DOCUMENT_TYPES).flatMap(([category, items]) =>
    items.map((item) => ({ ...item, category }))
  );

  const missingTypes = new Set(
    (compliance?.missing_documents || [])
      .map((entry) => (typeof entry === 'string' ? entry : entry?.document_type))
      .filter(Boolean)
  );

  const expiringOrExpired = new Set([
    ...(compliance?.expiring_documents || []).map((entry) => entry?.document_type).filter(Boolean),
    ...(compliance?.expired_documents || []).map((entry) => entry?.document_type).filter(Boolean),
  ]);

  const evidenceDocs = new Set([
    'RightToRentDoc',
    'HowToRent',
    'RRAInformationSheet',
    'ICORegistration',
    'DispatchReceipt',
    'Other',
  ]);

  const relevant = allDocs.filter((doc) => {
    if (missingTypes.has(doc.value) || expiringOrExpired.has(doc.value) || evidenceDocs.has(doc.value)) {
      return true;
    }
    return shouldShowByRule(doc.value, property);
  });

  const requiredFirst = relevant.sort((a, b) => {
    const aPriority = missingTypes.has(a.value) ? 0 : expiringOrExpired.has(a.value) ? 1 : 2;
    const bPriority = missingTypes.has(b.value) ? 0 : expiringOrExpired.has(b.value) ? 1 : 2;
    if (aPriority !== bPriority) return aPriority - bPriority;
    return a.label.localeCompare(b.label);
  });

  return {
    requiredNow: requiredFirst.filter((d) => missingTypes.has(d.value)),
    recommended: requiredFirst,
    missingTypes,
  };
};

export const DocumentUploadModal = ({ propertyId, property, compliance, tenancies = [], defaultTenancyId = null, forcedDocumentType = null, forcedCustomLabel = null, open, onOpenChange, onSuccess }) => {
  const [documentType, setDocumentType] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState('');
  const [selectedTenancyId, setSelectedTenancyId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const documentConfig = getDocumentConfig(documentType);
  const hasExpiry = documentConfig?.expiryMonths;
  const requiresCustomLabel = documentType === 'DispatchReceipt' || documentType === 'Other';
  const isCustomLabelLocked = Boolean(forcedCustomLabel);
  const dynamic = getDynamicDocumentTypes(property, compliance);
  const eligibleTenancies = tenancies.filter((tenancy) => !tenancy?.is_manual && isObjectIdLike(tenancy?.id));
  const hasManualTenancies = tenancies.some((tenancy) => tenancy?.is_manual);
  const isTenancyScopedUpload = TENANCY_SCOPED_DOCUMENT_TYPES.has(documentType);

  useEffect(() => {
    if (open && forcedDocumentType) {
      setDocumentType(forcedDocumentType);
    }
  }, [open, forcedDocumentType]);

  useEffect(() => {
    if (open) {
      setCustomLabel(forcedCustomLabel || '');
    }
  }, [open, forcedCustomLabel]);

  useEffect(() => {
    if (!open) return;

    if (!isTenancyScopedUpload) {
      setSelectedTenancyId('');
      return;
    }

    const preferredTenancyId = [defaultTenancyId, selectedTenancyId].find((value) => eligibleTenancies.some((tenancy) => tenancy.id === value));
    if (preferredTenancyId) {
      setSelectedTenancyId(preferredTenancyId);
      return;
    }

    if (eligibleTenancies.length === 1) {
      setSelectedTenancyId(eligibleTenancies[0].id);
    }
  }, [defaultTenancyId, eligibleTenancies, isTenancyScopedUpload, open, selectedTenancyId]);

  const forcedOption = forcedDocumentType
    ? getDocumentConfig(forcedDocumentType) || { value: forcedDocumentType, label: forcedDocumentType }
    : null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      // Validate file type (PDF, DOC, DOCX, JPG, PNG)
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
      ];

      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Invalid file type. Allowed: PDF, DOC, DOCX, JPG, PNG');
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!documentType) {
      toast.error('Please select a document type');
      return;
    }

    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    if (hasExpiry && !issueDate) {
      toast.error('Please provide the issue date for this document');
      return;
    }

    if (requiresCustomLabel && !customLabel.trim()) {
      toast.error('Please add a label for this document');
      return;
    }

    if (isTenancyScopedUpload && eligibleTenancies.length > 0 && !selectedTenancyId) {
      toast.error('Please select which tenancy this document belongs to');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setUploadProgress(0);

      const params = {
        document_type: documentType,
        property_id: propertyId,
      };

      if (issueDate) {
        params.issue_date = issueDate;
      }

      if (customLabel.trim()) {
        params.custom_label = customLabel.trim();
      }

      if (isTenancyScopedUpload && selectedTenancyId) {
        params.tenancy_id = selectedTenancyId;
      }

      const response = await axios.post(
        `${API_URL}/api/documents/upload`,
        formData,
        {
          params,
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        }
      );

      toast.success('Document uploaded successfully');
      setFile(null);
      setDocumentType('');
      setCustomLabel('');
      setIssueDate('');
      setSelectedTenancyId('');
      setUploadProgress(0);
      onOpenChange(false);

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      let errorMsg = 'Failed to upload document';
      
      // Handle different error response formats
      if (error.response?.data?.detail) {
        // Standard error with detail message
        errorMsg = error.response.data.detail;
      } else if (Array.isArray(error.response?.data)) {
        // Pydantic validation errors (array)
        errorMsg = error.response.data[0]?.msg || 'Validation error';
      } else if (error.response?.data?.msg) {
        // Single validation error object
        errorMsg = error.response.data.msg;
      }
      
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleOpenChange = (newOpen) => {
    if (!newOpen) {
      setFile(null);
      setDocumentType('');
      setCustomLabel('');
      setIssueDate('');
      setSelectedTenancyId('');
      setUploadProgress(0);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a new document for this property
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Document Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="document-type">Document Type *</Label>
            <Select disabled={uploading || Boolean(forcedDocumentType)} value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger id="document-type">
                <SelectValue placeholder="Select a document type" />
              </SelectTrigger>
              <SelectContent>
                {forcedOption ? (
                  <div>
                    <div className="px-2 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50">
                      Required for this remediation step
                    </div>
                    <SelectItem key={`forced-${forcedOption.value}`} value={forcedOption.value}>
                      {forcedOption.label}
                    </SelectItem>
                  </div>
                ) : (
                  <>
                    {dynamic.requiredNow.length > 0 && (
                      <div>
                        <div className="px-2 py-1.5 text-sm font-semibold text-red-700 bg-red-50">
                          Required now (compliance gaps)
                        </div>
                        {dynamic.requiredNow.map((type) => (
                          <SelectItem key={`req-${type.value}`} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </div>
                    )}
                    <div>
                      <div className="px-2 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100">
                        Recommended for this property
                      </div>
                      {dynamic.recommended.map((type) => (
                        <SelectItem key={`rec-${type.value}`} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </div>
                  </>
                )}
              </SelectContent>
            </Select>
            {forcedOption ? (
              <p className="text-xs text-blue-700">
                Document type is locked for this remediation upload.
              </p>
            ) : dynamic.requiredNow.length > 0 && (
              <p className="text-xs text-red-600">
                {dynamic.requiredNow.length} document type(s) currently required by compliance rules.
              </p>
            )}
          </div>

          {isTenancyScopedUpload && (
            <div className="space-y-2">
              <Label htmlFor="tenancy-scope">Tenancy</Label>
              <Select value={selectedTenancyId} onValueChange={setSelectedTenancyId} disabled={uploading || eligibleTenancies.length === 0}>
                <SelectTrigger id="tenancy-scope">
                  <SelectValue placeholder={eligibleTenancies.length === 0 ? 'No portal tenancy available' : 'Select tenancy'} />
                </SelectTrigger>
                <SelectContent>
                  {eligibleTenancies.map((tenancy) => (
                    <SelectItem key={tenancy.id} value={tenancy.id}>
                      {(Array.isArray(tenancy.tenants) ? tenancy.tenants.map((tenant) => tenant?.name).filter(Boolean).join(', ') : '') || 'Tenant'}{tenancy.start_date ? ` · ${formatDateForDisplay(tenancy.start_date)}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {eligibleTenancies.length > 0 ? (
                <p className="text-xs text-blue-700">
                  This document will be linked to the selected tenancy so multiple tenancies on the same property can be managed separately.
                </p>
              ) : (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <p className="font-medium">Documents are stored at property level for manual tenancies.</p>
                  <p className="mt-1">
                    Tenancy-level scoping is limited to portal tenancies because SIYAK can link those documents to tenant dashboard access and visibility records.
                  </p>
                </div>
              )}
              {hasManualTenancies ? (
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <p>
                    Manual tenancy uploads stay attached to the property, so use clear document labels with tenant names or dates when there are multiple tenancies.
                  </p>
                </div>
              ) : null}
            </div>
          )}

          {requiresCustomLabel && (
            <div className="space-y-2">
              <Label htmlFor="custom-label">Document Label *</Label>
              <input
                id="custom-label"
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                disabled={uploading || isCustomLabelLocked}
                maxLength={120}
                placeholder="e.g. RRA delivery record - John Smith - 29 Mar 2026"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {isCustomLabelLocked && (
                <p className="text-xs text-blue-700">Document label is locked for this remediation upload.</p>
              )}
            </div>
          )}

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file">File *</Label>
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="file"
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  uploading
                    ? 'border-gray-300 bg-gray-50'
                    : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {file ? (
                    <>
                      <CheckCircle className="w-8 h-8 mb-2 text-green-500" />
                      <p className="text-sm text-gray-600 font-medium">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mb-2 text-gray-600" />
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Click to upload</span> or
                        drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                      </p>
                    </>
                  )}
                </div>
                <input
                  id="file"
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  disabled={uploading}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </label>
            </div>
          </div>

          {/* Issue Date - Only for documents with expiry */}
          {hasExpiry && (
            <div className="space-y-2">
              <Label htmlFor="issue-date">
                Issue Date * 
                <span className="text-xs text-gray-500 font-normal ml-1">
                  (Expiry will be calculated automatically)
                </span>
              </Label>
              <input
                id="issue-date"
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                disabled={uploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Upload Progress */}
          {uploading && uploadProgress > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Uploading...</span>
                <span className="text-sm font-medium text-gray-900">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              Make sure your document is clear and readable. Upload will be verified
              for authenticity.
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={uploading || !documentType || !file}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
